// Assembles the FieldFlow-native Situation layer from canonical fixtures.
// This module is the concrete demonstration of FF01_PRODUCT_MODEL.md's
// "assembled, not replaced" rule: every Situation below is built by reading
// several canonical facts and producing an intervention context — it never
// edits the canonical records it references.
import { stepStatus, tourMachine, workOrderMachine } from "./stateMachines";
import type {
  COI,
  Lease,
  MoveInPlan,
  MoveOutPlan,
  Receivable,
  Situation,
  Tour,
  Unit,
  WorkOrder
} from "./types";
import { TODAY } from "./fixtures";

function daysBetween(a: string, b: string): number {
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / 86_400_000);
}

export function buildInitialSituations(input: {
  units: Unit[];
  leases: Lease[];
  receivables: Receivable[];
  workOrders: WorkOrder[];
  tours: Tour[];
  cois: COI[];
  moveInPlans: MoveInPlan[];
  moveOutPlans: MoveOutPlan[];
}): Situation[] {
  const { units, leases, receivables, workOrders, tours, cois, moveInPlans, moveOutPlans } = input;
  const unitById = new Map(units.map((u) => [u.id, u]));
  const leaseByUnit = new Map(leases.map((l) => [l.unitId, l]));
  const situations: Situation[] = [];

  // --- Hartley: the flagship COMPOSITE situation ---------------------------
  const hartleyUnit = unitById.get("unit-8203")!;
  const hartleyLease = leaseByUnit.get("unit-8203")!;
  const hartleyAr = receivables.find((r) => r.leaseId === hartleyLease.id)!;
  const hartleyWo = workOrders.find((w) => w.id === "wo-1187")!;
  const renewalDays = daysBetween(TODAY, hartleyLease.renewalWindowOpensDate);

  situations.push({
    id: "sit-hartley",
    siteId: hartleyUnit.siteId,
    unitId: hartleyUnit.id,
    form: "action",
    category: "financial",
    priority: "critical",
    title: "Hartley Cold Chain — AR + renewal",
    summary: `$${hartleyAr.balanceDue.toLocaleString()} late ${hartleyAr.daysLate} days — renewal opens in ${renewalDays} days.`,
    sources: [
      { kind: "receivable", id: hartleyAr.id, label: `$${hartleyAr.balanceDue.toLocaleString()} balance, ${hartleyAr.daysLate} days late` },
      { kind: "lease", id: hartleyLease.id, label: `Renewal window opens ${hartleyLease.renewalWindowOpensDate}` },
      { kind: "work-order", id: hartleyWo.id, label: "Compressor fault — repaired Aug 22, not yet verified" }
    ],
    jarvis: [],
    workflow: null,
    scheduledAt: null,
    status: "open",
    owner: "dm",
    createdAt: "2026-08-28T09:15:00",
    resolvedAt: null,
    needsAttention: true,
    linkedWorkOrderId: hartleyWo.id
  });

  // --- Work order situations (TRACKED) --------------------------------------
  for (const wo of workOrders) {
    if (wo.status === "closed") continue;
    if (wo.id === "wo-1187") continue; // folded into the Hartley composite situation above
    const unit = unitById.get(wo.unitId)!;
    situations.push({
      id: `sit-${wo.id}`,
      siteId: unit.siteId,
      unitId: unit.id,
      form: "tracked",
      category: "work-order",
      priority: wo.status === "ready-to-verify" ? "high" : "medium",
      title: `${wo.title} — ${wo.status === "ready-to-verify" ? "verify completion" : "in progress"}`,
      summary: wo.status === "ready-to-verify" ? "Vendor marked done — awaiting DM verification." : wo.description,
      sources: [{ kind: "work-order", id: wo.id, label: wo.title }],
      jarvis: [],
      workflow: { machine: "work-order", steps: stepStatus(workOrderMachine, wo.status) },
      scheduledAt: null,
      status: "open",
      owner: "dm",
      createdAt: wo.reportedAt,
      resolvedAt: null,
      needsAttention: wo.status === "ready-to-verify",
      linkedWorkOrderId: wo.id
    });
  }

  // --- Tour situations (TIMED, tracked workflow underneath) -----------------
  for (const tour of tours) {
    if (tour.status === "closed-won" || tour.status === "closed-lost") continue;
    const unit = unitById.get(tour.unitId)!;
    situations.push({
      id: `sit-tour-${tour.prospectCompany.toLowerCase().includes("brightline") ? "brightline" : "avon"}`,
      siteId: tour.siteId,
      unitId: unit.id,
      form: "timed",
      category: "tour",
      priority: tour.leadTemperature === "hot" ? "high" : "medium",
      title: `Tour prep — ${tour.prospectCompany}`,
      summary: `${tour.useCase} · ${tour.budgetRange}`,
      sources: [{ kind: "tour", id: tour.id, label: `${tour.source === "hubspot" ? "HubSpot" : "Google Calendar"} · ${tour.leadTemperature} lead` }],
      jarvis: [],
      workflow: { machine: "tour", steps: stepStatus(tourMachine, tour.status) },
      scheduledAt: tour.scheduledAt,
      status: "open",
      owner: "dm",
      createdAt: tour.scheduledAt,
      resolvedAt: null,
      needsAttention: tour.status === "scheduled",
      linkedTourId: tour.id
    });
  }

  // --- Move-in readiness (TRACKED) ------------------------------------------
  for (const plan of moveInPlans) {
    if (plan.status === "moved-in") continue;
    const unit = unitById.get(plan.unitId)!;
    const openCount = plan.items.filter((i) => !i.done).length;
    situations.push({
      id: `sit-${plan.id}`,
      siteId: unit.siteId,
      unitId: unit.id,
      form: "tracked",
      category: "move-in",
      priority: openCount > 0 ? "high" : "medium",
      title: `Move-in readiness — ${unit.unitNumber}`,
      summary: `${openCount} of ${plan.items.length} readiness items open · target ${plan.targetMoveInDate}`,
      sources: [{ kind: "move-in-plan", id: plan.id, label: `${plan.items.length - openCount}/${plan.items.length} readiness items complete` }],
      jarvis: [],
      workflow: { machine: "move-in", steps: moveInStepStatus(plan) },
      scheduledAt: null,
      status: "open",
      owner: "dm",
      createdAt: "2026-08-20T09:00:00",
      resolvedAt: null,
      needsAttention: openCount > 0,
      linkedMoveInPlanId: plan.id
    });
  }

  // --- Move-out / turnover (TRACKED) ---------------------------------------
  for (const plan of moveOutPlans) {
    if (plan.stage === "ready") continue;
    const unit = unitById.get(plan.unitId)!;
    situations.push({
      id: `sit-${plan.id}`,
      siteId: unit.siteId,
      unitId: unit.id,
      form: "tracked",
      category: "move-out",
      priority: "medium",
      title: `Turnover — ${unit.unitNumber}`,
      summary: `Stage: ${plan.stage.replace(/-/g, " ")}`,
      sources: [{ kind: "move-out-plan", id: plan.id, label: `Notice ${plan.noticeDate} → move-out ${plan.moveOutDate}` }],
      jarvis: [],
      workflow: { machine: "move-out", steps: moveOutStepStatus(plan.stage) },
      scheduledAt: null,
      status: "open",
      owner: "dm",
      createdAt: plan.noticeDate + "T09:00:00",
      resolvedAt: null,
      needsAttention: false,
      linkedMoveOutPlanId: plan.id
    });
  }

  // --- COI compliance (TRACKED, quiet unless flagged) -----------------------
  for (const coi of cois) {
    if (coi.status === "verified") continue;
    const unit = unitById.get(coi.unitId)!;
    situations.push({
      id: `sit-${coi.id}`,
      siteId: unit.siteId,
      unitId: unit.id,
      form: "tracked",
      category: "compliance",
      priority: "low",
      title: `COI renewal — ${unit.unitNumber}`,
      summary: `Expires ${coi.expiresOn} · reminder drafted, not sent`,
      sources: [{ kind: "coi", id: coi.id, label: `Certificate of insurance expires ${coi.expiresOn}` }],
      jarvis: [],
      workflow: { machine: "coi", steps: coiStepStatus(coi.status) },
      scheduledAt: null,
      status: "open",
      owner: "dm",
      createdAt: "2026-08-15T09:00:00",
      resolvedAt: null,
      needsAttention: false,
      linkedCoiId: coi.id
    });
  }

  return situations;
}

// Small local helper kept out of stateMachines.ts because move-in's
// "readiness" state is derived from plan-specific checklist completion
// rather than a single current-state enum on the plan itself.
function moveInStepStatus(plan: MoveInPlan) {
  const states: Array<"lease-signed" | "readiness" | "walkthrough" | "ready" | "moved-in"> = [
    "lease-signed",
    "readiness",
    "walkthrough",
    "ready",
    "moved-in"
  ];
  const labels = {
    "lease-signed": "Lease Signed",
    readiness: "Readiness",
    walkthrough: "Walkthrough",
    ready: "Ready",
    "moved-in": "Move-In"
  };
  const allReady = plan.status === "ready" || plan.status === "moved-in";
  const current: (typeof states)[number] = plan.status === "moved-in" ? "moved-in" : plan.status === "ready" ? "walkthrough" : "readiness";
  const idx = states.indexOf(current);
  return states.map((s, i) => ({ key: s, label: labels[s], done: i < idx || (allReady && s === "readiness"), current: i === idx }));
}

function moveOutStepStatus(current: MoveOutPlan["stage"]) {
  const states: MoveOutPlan["stage"][] = ["notice", "pre-walk", "move-out-inspection", "turnover-actions", "ready"];
  const labels: Record<MoveOutPlan["stage"], string> = {
    notice: "Notice",
    "pre-walk": "Pre-Walk",
    "move-out-inspection": "Move-Out Inspection",
    "turnover-actions": "Turnover Actions",
    ready: "Ready"
  };
  const idx = states.indexOf(current);
  return states.map((s, i) => ({ key: s, label: labels[s], done: i < idx, current: i === idx }));
}

function coiStepStatus(current: COI["status"]) {
  const states: COI["status"][] = ["flagged", "notified", "received", "verified"];
  const labels: Record<COI["status"], string> = { flagged: "Flagged", notified: "Notified", received: "Received", verified: "Verified" };
  const idx = states.indexOf(current);
  return states.map((s, i) => ({ key: s, label: labels[s], done: i < idx, current: i === idx }));
}

export function rankSituations(situations: Situation[]): Situation[] {
  const priorityWeight: Record<Situation["priority"], number> = { critical: 0, high: 1, medium: 2, low: 3 };
  return [...situations]
    .filter((s) => s.status === "open")
    .sort((a, b) => {
      if (a.needsAttention !== b.needsAttention) return a.needsAttention ? -1 : 1;
      return priorityWeight[a.priority] - priorityWeight[b.priority];
    });
}
